import React, { useState, useEffect } from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { styled } from '@mui/material/styles';
import {
  Button,
  CircularProgress,
  Typography,
  Paper,
  Box,
  Alert,
  Divider
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BusinessIcon from '@mui/icons-material/Business';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  v2SessionInfo,
  v2GetDivisions,
  v2SessionToken,
  v2IssueToken,
  DivisionMembership,
} from 'src/store/modules/auth/v2Operations';
import axios from 'axios';
import { getApiHost } from 'src/utils/ssoReferrer';
import { parseQueryString } from 'src/utils/browser';
import { saveDivisions, saveCurrentDivisionId } from 'src/utils/divisionStorage';
import { getAppUrlForReferrer, getReferrerInfo } from 'src/utils/ssoReferrer';
import { isStacklineUser } from 'src/utils/app';

const PageContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  backgroundColor: '#f5f5f5',
  padding: '20px'
});

const ContentBox = styled(Box)({
  maxWidth: '550px',
  width: '100%',
  backgroundColor: '#fff',
  borderRadius: '12px',
  padding: '32px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
});

const LoadingContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '300px',
  gap: '16px'
});

const SectionTitle = styled(Typography)({
  fontWeight: 600,
  marginBottom: '16px',
  color: '#1a1a2e'
});

const AppList = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  marginBottom: '24px'
});

const AppCard = styled(Paper)({
  padding: '12px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f5f9ff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  }
});

const AppInfo = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px'
});

const AppIcon = styled('div')<{ appColor: string }>(({ appColor }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '8px',
  backgroundColor: appColor,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  fontWeight: 700,
  fontSize: '16px'
}));

const DivisionList = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
});

const DivisionCard = styled(Paper)<{ selected?: boolean }>(({ selected }) => ({
  padding: '12px 16px',
  cursor: 'pointer',
  border: selected ? '2px solid #1976d2' : '2px solid transparent',
  backgroundColor: selected ? '#f5f9ff' : '#fff',
  transition: 'all 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  '&:hover': {
    backgroundColor: selected ? '#f5f9ff' : '#f5f5f5'
  }
}));

const DivisionIcon = styled('div')<{ selected?: boolean }>(({ selected }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '8px',
  backgroundColor: selected ? '#1976d2' : '#e0e0e0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: selected ? '#fff' : '#666'
}));

const RoleBadge = styled('span')({
  padding: '2px 8px',
  borderRadius: '4px',
  fontSize: '11px',
  fontWeight: 500,
  backgroundColor: '#e3f2fd',
  color: '#1976d2',
  textTransform: 'capitalize'
});

const APP_CONFIG: Record<string, { name: string; color: string; letter: string }> = {
  atlas: { name: 'Atlas', color: '#1976d2', letter: 'A' },
  beacon: { name: 'Beacon', color: '#2e7d32', letter: 'B' },
  advertising: { name: 'Advertising', color: '#9c27b0', letter: 'Ad' },
  ad: { name: 'Advertising', color: '#9c27b0', letter: 'Ad' },
  admanager: { name: 'Ad Manager', color: '#9c27b0', letter: 'AM' },
  advisor: { name: 'Advisor', color: '#1a1a2e', letter: 'Av' },
  omni: { name: 'Omni', color: '#f57c00', letter: 'O' },
  discover: { name: 'Discover', color: '#00bcd4', letter: 'D' }
};

const SwitchDivision: React.FC<RouteComponentProps> = ({ history }) => {
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [activeDivisionId, setActiveDivisionId] = useState<string | null>(null);
  const [divisions, setDivisions] = useState<DivisionMembership[]>([]);
  const [selectedDivisionId, setSelectedDivisionId] = useState<string>('');

  const queryParams = parseQueryString();
  const deniedApp = (typeof queryParams.app === 'string' ? queryParams.app : 'atlas').toLowerCase();
  const returnUrl = typeof queryParams.returnUrl === 'string' ? queryParams.returnUrl : null;

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Bootstrap: parallel session-info (liveness + active divisionId),
      // /v2/divisions (full list), and /v2/config (user email for display).
      // Replaces the previous single /v2/validate call.
      const baseUrl = getApiHost();
      const [sessionInfo, divisionsResp, configResp] = await Promise.all([
        v2SessionInfo(),
        v2GetDivisions().catch(() => null),
        axios
          .get(`https://${baseUrl}/api/auth/v2/config`, { withCredentials: true })
          .then(r => r.data)
          .catch(() => null),
      ]);

      if (!sessionInfo.authenticated) {
        // Stale / expired / revoked cookie. Route through /sign_out so
        // SignOut.tsx revokes the JTI, clears cookies, and tears down
        // Cognito's hosted-UI session before landing the user back at
        // signin. Mirrors App_v2.jsx's 401 handler.
        history.push('/sign_out');
        return;
      }

      setActiveDivisionId(sessionInfo.divisionId ?? null);

      // The C# DTO uses PascalCase property names but Newtonsoft + [JsonProperty]
      // serializes to camelCase: `session.userMetaData.email`. Read accordingly.
      const session = configResp?.session;
      const email = session?.userMetaData?.email || session?.email || '';
      setUserEmail(email);

      // Internal-tools gate. The cross-division auto-switch in NoAccessRedirect
      // covers every legitimate end-user case for picking a different division;
      // /switch-division is preserved as a Stackline-internal escape hatch only.
      // Anyone else who lands here (stale bookmark, direct nav) is signed out so
      // they restart from a clean session -- no bespoke "page not available" UI.
      if (email && !isStacklineUser(email)) {
        history.push('/sign_out');
        return;
      }

      if (divisionsResp?.divisions && divisionsResp.divisions.length > 0) {
        setDivisions(divisionsResp.divisions);
        setSelectedDivisionId(sessionInfo.divisionId || divisionsResp.divisions[0].id);
        // Save divisions to localStorage for nav bar dropdown
        saveDivisions(divisionsResp.divisions);
      } else {
        setError('No divisions found for your account.');
      }
    } catch (err: any) {
      console.error('Failed to load session:', err);
      if (err.response?.status === 401) {
        history.push('/sign_out');
      } else {
        setError('Failed to load your account information. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getAvailableApps = (division: DivisionMembership | undefined): string[] => {
    if (!division?.appAccess) return [];
    return Object.keys(division.appAccess).filter((app) => app !== deniedApp && division.appAccess[app] !== 'blocked');
  };

  const getAppUrl = (appName: string): string => {
    const appKey = appName === 'advertising' ? 'ad' : appName;
    const { stage } = getReferrerInfo();
    const referrer = stage === 'prod' ? appKey : `${appKey}-${stage}`;
    return getAppUrlForReferrer(referrer);
  };

  const handleAppClick = (appName: string) => {
    window.location.href = getAppUrl(appName);
  };

  const handleDivisionSelect = (divisionId: string) => {
    setSelectedDivisionId(divisionId);
  };

  const handleSwitchDivision = async () => {
    if (!selectedDivisionId) {
      setError('Unable to switch division. Please sign in again.');
      return;
    }

    try {
      setSwitching(true);
      setError(null);

      // Mint a fresh one-shot session token, then exchange it for a JWT
      // bound to the selected division. Replaces the legacy pattern of
      // pulling sessionToken out of the v2Validate response on mount and
      // hoping it hasn't expired (5 min TTL) by the time the user clicks.
      const tokenResp = await v2SessionToken();
      if (!tokenResp?.sessionToken) {
        setError('Unable to switch division. Please sign in again.');
        setSwitching(false);
        return;
      }

      await v2IssueToken(tokenResp.sessionToken, selectedDivisionId, deniedApp);

      // Save current division to localStorage
      saveCurrentDivisionId(selectedDivisionId);

      if (returnUrl) {
        window.location.href = decodeURIComponent(returnUrl);
      } else {
        window.location.href = getAppUrl(deniedApp);
      }
    } catch (err: any) {
      console.error('Failed to switch division:', err);
      setError(err.message || 'Failed to switch division. Please try again.');
      setSwitching(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <CircularProgress size={48} />
          <Typography variant="body1" color="text.secondary">
            Loading your account...
          </Typography>
        </LoadingContainer>
      </PageContainer>
    );
  }

  const currentDivision = divisions.find((d) => d.id === activeDivisionId);
  const availableApps = getAvailableApps(currentDivision);
  const deniedAppConfig = APP_CONFIG[deniedApp] || { name: deniedApp, color: '#666', letter: '?' };
  
  // Check if user actually has access to the app in their current division
  const hasAppAccessInCurrentDivision = currentDivision?.appAccess?.[deniedApp] && currentDivision.appAccess[deniedApp] !== 'blocked';

  return (
    <PageContainer>
      <ContentBox>
        {hasAppAccessInCurrentDivision ? (
          // User HAS access - show clean division picker (no error)
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <BusinessIcon sx={{ fontSize: 56, color: '#1976d2', mb: 2 }} />
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Switch Division
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Signed in as {userEmail}
            </Typography>
          </Box>
        ) : (
          // User does NOT have access - show error UI
          <>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <ErrorOutlineIcon sx={{ fontSize: 56, color: '#f44336', mb: 2 }} />
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Access Not Available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Signed in as {userEmail}
              </Typography>
            </Box>

            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                You don&apos;t have access to <strong>{deniedAppConfig.name}</strong> in your current division
                {currentDivision && <> (<strong>{currentDivision.name}</strong>)</>}.
              </Typography>
            </Alert>
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Only show "Available apps" section when user doesn't have access */}
        {!hasAppAccessInCurrentDivision && availableApps.length > 0 && (
          <>
            <SectionTitle variant="subtitle1">
              Available apps in {currentDivision?.name || 'your division'}
            </SectionTitle>
            <AppList>
              {availableApps.map((appKey) => {
                const appConfig = APP_CONFIG[appKey] || { name: appKey, color: '#666', letter: '?' };
                return (
                  <AppCard key={appKey} elevation={1} onClick={() => handleAppClick(appKey)}>
                    <AppInfo>
                      <AppIcon appColor={appConfig.color}>{appConfig.letter}</AppIcon>
                      <Typography fontWeight={500}>{appConfig.name}</Typography>
                    </AppInfo>
                    <OpenInNewIcon sx={{ color: '#666' }} />
                  </AppCard>
                );
              })}
            </AppList>
            <Divider sx={{ my: 3 }} />
          </>
        )}

        <SectionTitle variant="subtitle1">
          {hasAppAccessInCurrentDivision ? 'Select a division' : 'Or switch to a different division'}
        </SectionTitle>
        <DivisionList>
          {divisions.map((division) => {
            const isSelected = selectedDivisionId === division.id;
            const hasAppAccess = division.appAccess && division.appAccess[deniedApp] && division.appAccess[deniedApp] !== 'blocked';

            return (
              <DivisionCard
                key={division.id}
                selected={isSelected}
                elevation={isSelected ? 2 : 1}
                onClick={() => handleDivisionSelect(division.id)}
              >
                <DivisionIcon selected={isSelected}>
                  <BusinessIcon fontSize="small" />
                </DivisionIcon>
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={500} fontSize="14px">
                    {division.companyName || division.name || division.id}
                  </Typography>
                  {division.companyName && (
                    <Typography fontSize="12px" fontWeight={500} sx={{ color: '#7E8FA8' }}>
                      {division.name}
                    </Typography>
                  )}
                  {hasAppAccess && (
                    <Box sx={{ mt: 0.5 }}>
                      <RoleBadge>
                        {deniedAppConfig.name}: {division.appAccess[deniedApp]}
                      </RoleBadge>
                    </Box>
                  )}
                </Box>
                {isSelected && <CheckCircleIcon sx={{ color: '#1976d2' }} />}
                {!hasAppAccess && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No {deniedAppConfig.name} access
                  </Typography>
                )}
              </DivisionCard>
            );
          })}
        </DivisionList>

        <Button
          variant="contained"
          fullWidth
          sx={{ mt: 3, py: 1.5, textTransform: 'none', fontWeight: 600 }}
          onClick={handleSwitchDivision}
          disabled={!selectedDivisionId || switching}
        >
          {switching ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            `Continue with ${divisions.find((d) => d.id === selectedDivisionId)?.name || 'selected division'}`
          )}
        </Button>
      </ContentBox>
    </PageContainer>
  );
};

export default withRouter(SwitchDivision);
